import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { productCatalogApi } from '../../api/productCatalog';
import { connectorsApi } from '../../api/connectors';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../../components/common/EmptyState';
import { getCompanyId } from '../../api/auth';

const sanitizeSlugSuffix = (v) =>
  String(v || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');

const emptyFieldMap = () => ({
  id: '',
  title: '',
  description: '',
  imageUrl: '',
  price: '',
  currency: '',
  quantity: '',
  branch: '',
});

const FIELD_MAP_LABELS = {
  id: 'id',
  title: 'nombre (title)',
  description: 'description',
  imageUrl: 'imageUrl',
  price: 'precio',
  currency: 'moneda',
  quantity: 'cantidad',
  branch: 'sucursal',
};

const K = {
  navy: '#121826',
  blue: '#4185F1',
  border: 'rgba(18, 24, 38, 0.08)',
  surface: '#F8FAFC',
};

const kobraPageSx = {
  width: '100%',
  maxWidth: 1200,
  p: 3,
  bgcolor: K.surface,
};

const kobraPrimaryBtnSx = {
  '&&': {
    borderRadius: 1.5,
    px: 2.5,
    py: 1.1,
    textTransform: 'none',
    fontWeight: 700,
    bgcolor: K.blue,
    color: '#fff',
    border: 'none',
    backgroundImage: 'none',
    boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
    '&:hover': {
      bgcolor: '#3574d9',
      border: 'none',
      backgroundImage: 'none',
      boxShadow: `0 6px 18px ${alpha(K.blue, 0.38)}`,
    },
  },
};

const kobraCardSx = {
  borderRadius: 2,
  border: `1px solid ${K.border}`,
  boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
  bgcolor: '#fff',
};

const kobraFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
    '&:hover fieldset': { borderColor: `${K.blue}55` },
    '&.Mui-focused fieldset': { borderColor: K.blue, borderWidth: 2 },
  },
};

const emptyForm = () => ({
  slugSuffix: '',
  title: '',
  connectorId: '',
  endpointId: '',
  itemsJsonPath: '',
  fieldMap: emptyFieldMap(),
  status: 'active',
});

function buildFieldMapPayload(form) {
  const out = {};
  const fm = form.fieldMap || {};
  Object.keys(fm).forEach((k) => {
    const v = (fm[k] || '').trim();
    if (v) out[k] = v;
  });
  return out;
}

function parseFieldMapToForm(row) {
  const fm = row?.fieldMap || {};
  const base = emptyFieldMap();
  Object.keys(base).forEach((k) => {
    if (fm[k] != null) base[k] = String(fm[k]);
  });
  return base;
}

/** DB may store suffix only, or legacy "idcompany-suffix". */
function pathSlugFromStoredPublicSlug(stored, cid) {
  if (!stored) return '';
  const s = stored.trim();
  const c = (cid || '').toLowerCase();
  const prefix = c ? `${c}-` : '';
  if (prefix && s.toLowerCase().startsWith(prefix)) {
    return s.slice(prefix.length);
  }
  return s;
}

export const ProductCatalogAdmin = () => {
  const companyId = (getCompanyId() || '').trim().toLowerCase();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const queryClient = useQueryClient();
  const { showToast, ToastComponent } = useToast();

  /** Suffix only — sent as publicSlug to API; URL is /catalog/idcompany/slug */
  const catalogSlugFromForm = () => sanitizeSlugSuffix(form.slugSuffix).replace(/^-+/, '');

  const { data: pages, isLoading } = useQuery({
    queryKey: ['productCatalogPages'],
    queryFn: () => productCatalogApi.listPages(),
  });

  const { data: connectors } = useQuery({
    queryKey: ['connectors'],
    queryFn: () => connectorsApi.getConnectors(),
  });

  const selectedConnector = useMemo(
    () => (connectors || []).find((c) => c.id === form.connectorId),
    [connectors, form.connectorId],
  );

  const endpointOptions = selectedConnector?.endpoints || [];

  const shareUrl = (cid, pathSlug) => {
    if (typeof window === 'undefined') return '';
    const c = (cid || '').trim();
    const s = (pathSlug || '').trim().toLowerCase();
    if (!c || !s) return '';
    return `${window.location.origin}/catalog/${encodeURIComponent(c)}/${encodeURIComponent(s)}`;
  };

  const copyShareUrl = (pathSlug) => {
    const url = shareUrl(companyId, pathSlug);
    navigator.clipboard.writeText(url).then(
      () => showToast('URL copiada al portapapeles'),
      () => showToast('No se pudo copiar', 'error'),
    );
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    const slugSuffix = pathSlugFromStoredPublicSlug(row.publicSlug, companyId);
    setForm({
      slugSuffix,
      title: row.title || '',
      connectorId: row.connectorId || '',
      endpointId: row.endpointId || '',
      itemsJsonPath: row.itemsJsonPath || '',
      fieldMap: parseFieldMapToForm(row),
      status: row.status || 'active',
    });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const fieldMap = buildFieldMapPayload(form);
      const publicSlug = catalogSlugFromForm();
      const payload = {
        publicSlug,
        title: form.title.trim(),
        connectorId: form.connectorId,
        endpointId: form.endpointId,
        itemsJsonPath: form.itemsJsonPath.trim(),
        fieldMap,
        status: form.status,
      };
      if (editingId) {
        return productCatalogApi.updatePage(editingId, payload);
      }
      return productCatalogApi.createPage(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCatalogPages'] });
      setDialogOpen(false);
      showToast(editingId ? 'Página actualizada' : 'Página creada');
    },
    onError: (e) => showToast(e.message || 'Error al guardar', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => productCatalogApi.deletePage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCatalogPages'] });
      showToast('Página eliminada');
    },
    onError: (e) => showToast(e.message || 'Error al eliminar', 'error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!companyId) {
      showToast('No se encontró el ID de compañía', 'error');
      return;
    }
    if (!catalogSlugFromForm()) {
      showToast('Defina el slug (segmento final de la URL)', 'error');
      return;
    }
    if (!form.title.trim() || !form.connectorId || !form.endpointId) {
      showToast('Complete título, connector y endpoint', 'error');
      return;
    }
    saveMutation.mutate();
  };

  return (
    <Box sx={kobraPageSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: alpha(K.blue, 0.12),
              display: 'flex',
            }}
          >
            <StorefrontIcon sx={{ color: K.blue }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: K.navy }}>
            Catálogo público
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={kobraPrimaryBtnSx}>
          Nueva página
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure páginas enlazadas a un connector. La URL pública no requiere inicio de sesión.
      </Typography>

      {isLoading ? (
        <Typography>Cargando…</Typography>
      ) : !pages?.length ? (
        <Card sx={kobraCardSx}>
          <CardContent>
            <EmptyState
              icon={StorefrontIcon}
              title="Sin páginas de catálogo"
              description="Cree una página y elija el endpoint que devuelve la lista de productos."
            />
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={kobraCardSx}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(K.blue, 0.03) }}>
                <TableCell>Título</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.title}</TableCell>
                  <TableCell>
                    <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                      {companyId}/{pathSlugFromStoredPublicSlug(p.publicSlug, companyId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.status === 'active' ? 'Activa' : 'Inactiva'}
                      color={p.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Copiar URL pública">
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyShareUrl(pathSlugFromStoredPublicSlug(p.publicSlug, companyId))
                        }
                        aria-label="copiar URL"
                        sx={{ color: K.blue, '&:hover': { bgcolor: alpha(K.blue, 0.1) } }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(p)}
                        sx={{ color: K.blue, '&:hover': { bgcolor: alpha(K.blue, 0.1) } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (window.confirm('¿Eliminar esta página?')) {
                            deleteMutation.mutate(p.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: `1px solid ${K.border}` } }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ borderBottom: `1px solid ${K.border}`, fontWeight: 800 }}>
            {editingId ? 'Editar página' : 'Nueva página'}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, bgcolor: '#fff' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                URL pública: <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>/catalog/</Box>
                <strong>idcompany</strong>
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>/slug</Box> (ej.{' '}
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  /catalog/550e8400-e29b-41d4-a716-446655440000/mi-catalogo
                </Box>
                )
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  mb: 0.5,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    wordBreak: 'break-all',
                  }}
                >
                  /catalog/{companyId || '…'}/
                </Typography>
                <TextField
                  label="Slug (segmento final)"
                  value={form.slugSuffix}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, slugSuffix: sanitizeSlugSuffix(e.target.value) }))
                  }
                  placeholder="mi-catalogo"
                  required
                  size="small"
                  sx={{ flex: 1, minWidth: 140, ...kobraFieldSx }}
                />
              </Box>
              {catalogSlugFromForm() ? (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vista previa · <strong>idcompany</strong>:{' '}
                    <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {companyId || '—'}
                    </Box>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    URL: {shareUrl(companyId, catalogSlugFromForm())}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={shareUrl(companyId, catalogSlugFromForm())}
                    sx={{ mt: 1, borderColor: K.border, color: K.navy }}
                  >
                    Abrir catálogo público
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${window.location.origin}/catalog-edit/${encodeURIComponent(
                      companyId,
                    )}/${encodeURIComponent(catalogSlugFromForm())}`}
                    sx={{ mt: 1, ml: 1, ...kobraPrimaryBtnSx }}
                  >
                    Editar imágenes en vista previa
                  </Button>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block">
                  Solo el último segmento (p. ej. <strong>mi-catalogo</strong>). La ruta completa es{' '}
                  <strong>/catalog/idcompany/slug</strong>.
                </Typography>
              )}
            </Box>
            <TextField
              label="Título"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              fullWidth
              sx={kobraFieldSx}
            />
            <FormControl fullWidth required>
              <InputLabel id="connector-label">Connector</InputLabel>
              <Select
                labelId="connector-label"
                label="Connector"
                value={form.connectorId}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    connectorId: e.target.value,
                    endpointId: '',
                  }))
                }
                sx={kobraFieldSx}
              >
                {(connectors || []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required disabled={!form.connectorId}>
              <InputLabel id="endpoint-label">Endpoint</InputLabel>
              <Select
                labelId="endpoint-label"
                label="Endpoint"
                value={form.endpointId}
                onChange={(e) => setForm((f) => ({ ...f, endpointId: e.target.value }))}
                sx={kobraFieldSx}
              >
                {endpointOptions.map((ep) => (
                  <MenuItem key={ep.id} value={ep.id}>
                    {ep.name} ({ep.method} {ep.path})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Ruta JSON a la lista (opcional)"
              value={form.itemsJsonPath}
              onChange={(e) => setForm((f) => ({ ...f, itemsJsonPath: e.target.value }))}
              helperText="Ej: data.items o vacío si la raíz es un arreglo"
              fullWidth
              sx={kobraFieldSx}
            />
            <Typography variant="subtitle2" color="text.secondary">
              Mapeo de campos (rutas en cada ítem, notación con puntos)
            </Typography>
            <Grid container spacing={1}>
              {['title', 'price', 'quantity', 'branch', 'imageUrl', 'description', 'id', 'currency'].map(
                (key) => (
                <Grid item key={key} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={FIELD_MAP_LABELS[key] || key}
                    value={form.fieldMap[key] || ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        fieldMap: { ...f.fieldMap, [key]: e.target.value },
                      }))
                    }
                    size="small"
                    fullWidth
                    sx={kobraFieldSx}
                  />
                </Grid>
              ))}
            </Grid>

            <FormControl fullWidth>
              <InputLabel id="status-label">Estado</InputLabel>
              <Select
                labelId="status-label"
                label="Estado"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                sx={kobraFieldSx}
              >
                <MenuItem value="active">Activa</MenuItem>
                <MenuItem value="inactive">Inactiva</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${K.border}`, px: 3, py: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saveMutation.isPending} sx={kobraPrimaryBtnSx}>
              Guardar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ToastComponent />
    </Box>
  );
};
